import type {
  TargetProbeLegMember,
  TargetProbeLegRecord,
  TargetProbeReachReason,
} from '@nadoutong/contracts';

/**
 * Where a physical leg's routing targets live, so an operator can jump from
 * 重点保障中心 to the place that actually holds them (#2602 follow-up).
 *
 * Reachability comes from the gateway (`member.reachable` / `reach_reason`,
 * the §1 ladder). A member from an older gateway without those fields reads
 * reachable: a leg is never hidden on missing evidence.
 */

export interface LegLocation {
  /** `default` or `config:<id>`; one entry per place, members folded in. */
  readonly key: string;
  readonly layer: 'default' | 'config';
  readonly configId: string | null;
  readonly configName: string | null;
  readonly deprecated: boolean;
  readonly assignedOrgs: number | null;
  readonly reachable: boolean;
  readonly reason: TargetProbeReachReason | null;
  readonly enabledTargets: number;
  readonly disabledTargets: number;
  readonly href: string;
}

export function memberReachable(member: TargetProbeLegMember): boolean {
  return member.reachable ?? true;
}

/** A leg takes traffic when any member does (an empty member list stays visible). */
export function legReachable(leg: TargetProbeLegRecord): boolean {
  return leg.members.length === 0 || leg.members.some(memberReachable);
}

/** Some traffic-taking member sits in a config marked deprecated (still has customers). */
export function legHasLiveDeprecatedConfig(leg: TargetProbeLegRecord): boolean {
  return leg.members.some((member) => memberReachable(member) && member.config_deprecated === true);
}

/**
 * Default-layer targets are the SKU's own default pool (tier 5), edited on
 * the SKU detail page's 供给 panel — the tenant routing page lists configs
 * only. Config targets open the config sheet with the SKU preselected.
 */
export function legLocationHref(skuId: string, layer: 'default' | 'config', configId: string | null): string {
  if (layer === 'default') return `/market/${encodeURIComponent(skuId)}`;
  if (configId === null) return '/supply/tenant-routing';
  const params = new URLSearchParams({ config: configId, sku: skuId });
  return `/supply/tenant-routing?${params.toString()}`;
}

const REASON_ORDER: readonly TargetProbeReachReason[] = [
  'global',
  'override',
  'org_assigned',
  'default_fallthrough',
  'unassigned_config',
  'shadowed_by_global_exclusive',
];

function reasonRank(reason: TargetProbeReachReason | null): number {
  const index = reason === null ? -1 : REASON_ORDER.indexOf(reason);
  return index < 0 ? REASON_ORDER.length : index;
}

export function legLocations(leg: TargetProbeLegRecord): readonly LegLocation[] {
  const byKey = new Map<string, LegLocation>();
  for (const member of leg.members) {
    const configId = member.layer === 'config' ? member.config_id ?? null : null;
    const key = member.layer === 'default' ? 'default' : `config:${configId ?? '?'}`;
    const existing = byKey.get(key);
    const reachable = memberReachable(member);
    if (existing === undefined) {
      byKey.set(key, {
        key,
        layer: member.layer,
        configId,
        configName: member.config_name ?? null,
        deprecated: member.config_deprecated === true,
        assignedOrgs: member.config_assigned_orgs ?? null,
        reachable,
        reason: member.reach_reason ?? null,
        enabledTargets: member.enabled ? 1 : 0,
        disabledTargets: member.enabled ? 0 : 1,
        href: legLocationHref(leg.sku_id, member.layer, configId),
      });
      continue;
    }
    byKey.set(key, {
      ...existing,
      reachable: existing.reachable || reachable,
      enabledTargets: existing.enabledTargets + (member.enabled ? 1 : 0),
      disabledTargets: existing.disabledTargets + (member.enabled ? 0 : 1),
    });
  }
  return [...byKey.values()].sort((left, right) =>
    Number(right.reachable) - Number(left.reachable)
    || reasonRank(left.reason) - reasonRank(right.reason)
    || Number(left.deprecated) - Number(right.deprecated)
    || (left.configName ?? '').localeCompare(right.configName ?? ''));
}

export interface LegMemberBreakdown {
  readonly total: number;
  readonly byReason: ReadonlyMap<TargetProbeReachReason | 'unknown', number>;
  readonly unreachable: number;
  /** Unreachable members whose config is deprecated. */
  readonly unreachableDeprecated: number;
}

export function legMemberBreakdown(leg: TargetProbeLegRecord): LegMemberBreakdown {
  const byReason = new Map<TargetProbeReachReason | 'unknown', number>();
  let unreachable = 0;
  let unreachableDeprecated = 0;
  for (const member of leg.members) {
    const reason = member.reach_reason ?? 'unknown';
    byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
    if (!memberReachable(member)) {
      unreachable += 1;
      if (member.config_deprecated === true) unreachableDeprecated += 1;
    }
  }
  return { total: leg.members.length, byReason, unreachable, unreachableDeprecated };
}

export type LegWeightSource = 'override' | 'global' | 'default';

/**
 * Where this leg's routing weight actually matters. The max across every
 * member reads 「权重 100」 on a leg that is 纯兜底 0 in the global config and
 * 100 only in some customer config; this names the weight in the layer that
 * serves most traffic and counts the customer configs where it leads.
 *
 * - `legacy`: members carry no reach fields (older gateway) → keep the max.
 * - `primary`: tier 0 override, else tier 4 global, else the SKU default
 *   fallback (tier 5) — only reachable members with a known weight.
 * - `orgFirstPick`: reachable org-assigned configs where the weight is > 0.
 * - `null`: no reachable member carries a weight — print nothing.
 */
export type LegWeightSummary =
  | { readonly kind: 'legacy' }
  | {
    readonly kind: 'placed';
    readonly primary: { readonly source: LegWeightSource; readonly weight: number } | null;
    readonly orgFirstPick: number;
    readonly allZero: boolean;
  };

function knownWeight(member: TargetProbeLegMember): number | null {
  const weight: unknown = member.weight;
  return typeof weight === 'number' && Number.isFinite(weight) ? weight : null;
}

export function legWeightSummary(leg: TargetProbeLegRecord): LegWeightSummary | null {
  if (!leg.members.some((member) => member.reach_reason !== undefined)) return { kind: 'legacy' };
  const weighted = leg.members.filter((member) => memberReachable(member) && knownWeight(member) !== null);
  if (weighted.length === 0) return null;
  const bySource = (reason: TargetProbeReachReason): number | null => {
    const weights = weighted.filter((member) => member.reach_reason === reason).map((member) => knownWeight(member) ?? 0);
    return weights.length === 0 ? null : Math.max(...weights);
  };
  const sources: readonly [LegWeightSource, TargetProbeReachReason][] = [
    ['override', 'override'],
    ['global', 'global'],
    ['default', 'default_fallthrough'],
  ];
  let primary: { readonly source: LegWeightSource; readonly weight: number } | null = null;
  for (const [source, reason] of sources) {
    const weight = bySource(reason);
    if (weight !== null) {
      primary = { source, weight };
      break;
    }
  }
  const orgConfigs = new Set(weighted
    .filter((member) => member.reach_reason === 'org_assigned' && (knownWeight(member) ?? 0) > 0)
    .map((member) => member.config_id ?? member.target_id));
  return {
    kind: 'placed',
    primary,
    orgFirstPick: orgConfigs.size,
    allZero: weighted.every((member) => knownWeight(member) === 0),
  };
}
